<script lang="ts">
  import FaArrowCircleLeft from "svelte-icons/fa/FaArrowCircleLeft.svelte";
  import FaCheckCircle from "svelte-icons/fa/FaCheckCircle.svelte";
  import FaWindowClose from "svelte-icons/fa/FaWindowClose.svelte";
  import FaInfoCircle from 'svelte-icons/fa/FaInfoCircle.svelte';
  import Button from "./button.svelte";
  import { t, locale } from "../translations/i18n";
  import { dictionaryStore } from "../data-views/stores";
  import { generateUID } from "../data-views/utils";
  import { tick } from 'svelte';

  type Options = {
    model?: string;
    name: { [key: string]: string };
    title?: { [key: string]: string };
    keywords?: string[] | string;
    selected?: boolean;
    disabled?: boolean;
    is_daily?: boolean;
  }[];

  export let options: Options;
  export let title: string;

  export let multiselect: boolean = false;
  export let showSelectButton: boolean = false;
  export let showCancelButton: boolean = false;
  export let visualizationId: string;
  let uniqueId: string = generateUID();

  let currentLanguage: any = 'en-US';
  let currentIndex = -1;
  let currentFilterPropsIndex = 0;
  let filterSelectedList: string[] = [];
  let dictionaryEntries: any[] = [];

  locale.subscribe(value => {
    currentLanguage = value.split("-")[0];
  });

  $: dictionaryEntries = $dictionaryStore.entries || [];

  const getOptionDisplayLabel = (option: any): string =>
    String(option.name?.[currentLanguage] || option.name?.en || "").trim();

  const getMatchedDictionaryKeyword = (option: any): string | null => {
    const displayLabel = getOptionDisplayLabel(option).trim();
    const normalizeForMatch = (s: any) => {
      const raw = String(s || "");
      try {
        return raw
          .toLowerCase()
          .replace(/[^^\p{L}\p{N}\s]/gu, "")
          .replace(/\s+/g, " ")
          .trim();
      } catch (e) {
        return raw
          .toLowerCase()
          .replace(/[^\w\s]|_/g, "")
          .replace(/\s+/g, " ")
          .trim();
      }
    };
    const normalizedLabel = normalizeForMatch(displayLabel);

    for (const entry of dictionaryEntries) {
      const keywords = entry.keywords.split(/\r?\n/).map((keyword: string) => keyword.trim());
      for (const keyword of keywords.filter((keyword: string) => keyword.length > 0)) {
        if (normalizeForMatch(keyword) === normalizedLabel) {
          return keyword;
        }
      }
    }

    return null;
  };

  const hasDictionaryEntry = (option: any) => Boolean(getMatchedDictionaryKeyword(option));

  const openDictionaryForOption = (option: any) => {
    const matchedKeyword = getMatchedDictionaryKeyword(option);
    if (!matchedKeyword) return;

    dictionaryStore.setSearch(matchedKeyword);
    dictionaryStore.openDictionary();
  };

  export let selectCallback: (
    title: string,
    selectedOptions: Options,
    selectButtonClicked: Boolean
  ) => void;

  export let cancelCallback: () => void = () => void 0;

  let filter = "";
  let filteredOptions = options;

  const getOptionKeywords = (option: any): string[] => {
    const raw = option.keywords;
    if (!raw) return [];
    if (!Array.isArray(raw)) return raw[currentLanguage] || raw["en"] || [];
    return raw;
  };

  const handleFilterInput = () => {
    if (filter === "") {
      filteredOptions = options;
      return;
    }

    const tokens = filter.toLowerCase().split(/\s+/).filter(Boolean);
    const fullQuery = filter.toLowerCase();

    const scored = options.map((option) => {
      const keywords = getOptionKeywords(option);
      const label = getOptionDisplayLabel(option).toLowerCase();
      const searchPool = [label, ...keywords.map((kw) => String(kw).toLowerCase())];

      // Require every token to be found somewhere
      if (!tokens.every((token) => searchPool.some((text) => text.includes(token)))) {
        return { option, score: 0 };
      }

      let score = 0;
      for (const token of tokens) {
        if (label.includes(token)) score += 2;
        if (keywords.some((kw) => String(kw).toLowerCase().includes(token))) score += 1;
      }
      if (label.startsWith(fullQuery)) score += 5;

      return { option, score };
    });

    filteredOptions = scored
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ option }) => option);
  };

  const handleFilterClear = () => {
    filter = "";
    handleFilterInput();
  };

  const handleOptionClick = (option: any) => {
    if (!option || option.disabled) return;

    if (!multiselect) {
      if (option.selected) {
        option.selected = false;
        selectCallback(title, [], false);
        return;
      }
      options.forEach((o) => (o.selected = false));
    }

    option.selected = !option.selected;
    filteredOptions = [...filteredOptions]; // update filteredOptions to trigger re-render

    const selectedOptions = multiselect
      ? options.filter((option) => option.selected)
      : [option];
    selectCallback(title, selectedOptions, false);
  };

  const handleSelectClick = () => {
    const selectedOptions = options.filter((option) => option.selected);
    selectCallback(title, selectedOptions, true);
  };

  const handleCancelClick = () => {
    options.forEach((option) => (option.selected = false));
    filter = "";
    filteredOptions = options;
    cancelCallback();
  };

  $: selectDisabled =
    filteredOptions.filter((option) => option.selected).length === 0;

  
  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = async (event: KeyboardEvent): Promise<void> => {
    await getFilterSelectedList();

    switch (event.key) {
      case "Enter":
        currentFilterPropsIndex = filterSelectedList.indexOf(`filterPropsContainer${uniqueId}`);
        let focusedElement = document.activeElement as HTMLElement;
        focusedElement?.click();
        // focus on the visualization container
        let parentVisualization = document.getElementById("container-id-" + visualizationId);
        parentVisualization?.focus();
        break;

      case "ArrowDown":
        if (!filteredOptions[currentIndex + 1]?.disabled){
          currentIndex = (currentIndex + 1) % filteredOptions.length;
          focusCurrentField();
        }
        else {
          currentIndex = 0;
          focusCurrentField();
        }
        break;

      case "ArrowUp":
        if (!filteredOptions[currentIndex - 1]?.disabled && currentIndex > 0){
          currentIndex = (currentIndex - 1 + filteredOptions.length) % filteredOptions.length;
          focusCurrentField();
        } else if (currentIndex === 0) {
          currentIndex = -1;
          // focus input field
          document.getElementById(`filter-props-input-${uniqueId}`)?.focus();
        }
        break;

      case "ArrowRight":
        currentFilterPropsIndex = filterSelectedList.indexOf(`filterPropsContainer${uniqueId}`);
        if (currentFilterPropsIndex < 0) {
          currentFilterPropsIndex = 0;
        }
        if (currentFilterPropsIndex > filterSelectedList.length - 1) {
          currentFilterPropsIndex =  filterSelectedList.length - 1;
          focusFilterPropsContainer(currentFilterPropsIndex);
        } else if (currentFilterPropsIndex === filterSelectedList.length - 1) {
          let parentVisualization = document.getElementById("container-id-" + visualizationId);
          if (parentVisualization) {
            let selectorButtons = Array.from(parentVisualization.querySelectorAll('[id^="AddData-"]'));
            (selectorButtons[0] as HTMLElement).focus();
          }
        }
        else {
          currentFilterPropsIndex++;
          focusFilterPropsContainer(currentFilterPropsIndex);
        }
        currentIndex = -1;
        break;

      case "ArrowLeft":
        currentFilterPropsIndex = filterSelectedList.indexOf(`filterPropsContainer${uniqueId}`);

        if (currentFilterPropsIndex <= 0) {
          currentFilterPropsIndex = 0;
        } else {
          currentFilterPropsIndex--;
        }
        currentIndex = -1;
        focusFilterPropsContainer(currentFilterPropsIndex);
        break;

      default:
        // Allow default behavior for other keys
        return;
    }

    // Prevent default behavior only for relevant keys
    event.preventDefault();
  };

  const getFilterSelectedList = async () => {
    await tick();
    let parentVisualization = document.getElementById("container-id-" + visualizationId);
    if (parentVisualization) {
      filterSelectedList = Array.from(parentVisualization.querySelectorAll('[id^="filterPropsContainer"]')).map((element) => element.id);
    } else {
      filterSelectedList = [];
    }
    return filterSelectedList;
  };

  /**
   * Focus the current field
   * @returns void
  */
  const focusCurrentField = () => {
    // At first get the current container, then get the current field
    const container = document.querySelector(`#filterPropsContainer${uniqueId}`);
    if (container) {
      const field = container.querySelector(`#fieldItem${currentIndex}`);
      if (field) {
        (field as HTMLElement).focus();
      }
    }
  };

  const focusFilterPropsContainer = (index: number) => {
    const elementId = filterSelectedList[index];
    const filterPropsContainer = document.getElementById(elementId);
    if (filterPropsContainer) {
      (filterPropsContainer as HTMLElement).focus();
    }
  };

</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div class="container" on:keydown={onKeyDown} tabindex="0" id="filterPropsContainer{uniqueId}">
  <div class="title">{title[currentLanguage] || title}</div>
  <div class="controls">
    <div class="filter">
      <input
        placeholder={$t("Type to search...")}
        bind:value={filter}
        on:input={handleFilterInput}
        title={$t("Type to search...")}
        id="filter-props-input-{uniqueId}"
      />
      {#if filter}
        <div class="icon" on:click={handleFilterClear} title="Clear">
          <FaWindowClose />
        </div>
      {/if}
    </div>

    {#if showCancelButton}
      <div class="cancel">
        <Button handler={handleCancelClick} title="Back">
          <svelte:fragment slot="icon"><FaArrowCircleLeft /></svelte:fragment>
        </Button>
      </div>
    {/if}

    {#if showSelectButton}
      <Button
        handler={handleSelectClick}
        disabled={selectDisabled}
        title="Select"
      >
        <svelte:fragment slot="icon"><FaCheckCircle /></svelte:fragment>
      </Button>
    {/if}
  </div>
  <ul tabindex="0" id="filterPropsUl">
    {#each filteredOptions as stat, index}
      <li
        title={stat.title ? stat.title[currentLanguage] : ''}
        on:click={() => handleOptionClick(stat)}
        class:selected={filteredOptions.find(
          (option) => option.name === stat.name
        )?.selected}
        class:disabled={stat.disabled}
        tabindex="0"
        id=fieldItem{index}
        class:focused={currentIndex === index}
        on:focus={() => currentIndex = index}
      >
        <div class="option-row">
          <span>{stat.name[currentLanguage]}</span>
          {#if hasDictionaryEntry(stat)}
            <span
              class="dictionary-indicator"
              role="button"
              tabindex="0"
              aria-label="Open dictionary"
              title={$t("Dictionary")}
              on:click|stopPropagation={() => openDictionaryForOption(stat)}
              on:keydown|stopPropagation={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  openDictionaryForOption(stat);
                }
              }}
            >
              <FaInfoCircle />
            </span>
          {/if}
        </div>
      </li>
    {/each}
  </ul>
</div>

<style lang="scss">
  @import "../scrollbars";

  .container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 128px;
    box-sizing: border-box;

    border: 1px solid var(--color-primary);
    border-radius: 1rem;
    padding: 1rem;

    .title {
      font-weight: bold;
      padding-bottom: 0.5rem;
      border-bottom: 3px solid var(--color-gray);
    }

    .controls {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin: 1rem 0;

      .filter {
        flex: 1;
        position: relative;

        input {
          width: 100%;
          min-width: 80px;
          box-sizing: border-box;
          padding: 0.5rem 2rem 0.5rem 0.5rem;

          font-size: 1rem;
          line-height: 1;

          border: 1px solid var(--color-white);
          border-radius: 0.25rem;

          color: var(--color-white);
        }

        .icon {
          position: absolute;
          right: 0.5rem;
          top: 0.75rem;
          color: var(--color-white);
        }
      }

      .cancel :global(button) {
        background-color: var(--color-tertiary);
      }

      :global(button) {
        background-color: var(--color-primary);
      }
    }

    ul {
      overflow-y: auto;
      overflow-x: hidden;

      display: flex;
      flex-direction: column;

      box-sizing: border-box;
      color: var(--color-white);

      list-style-type: none;
      font-size: 1rem;
      line-height: 1;
      padding: 0;
      margin: 0;

      @include scrollbars;

      li {
        padding: calc(0.5rem - 1px) 1rem;
        margin: 1px 0;
        white-space: nowrap;
        cursor: pointer;
        user-select: none;

        &.selected {
          color: hsl(149, 55%, 66%);
          background-color: #35b47266;
        }

        &.focused {
          background-color: var(--color-primary);
          outline: none;
        }

        &.disabled,
        &.disabled:hover {
          color: var(--color-gray-light);
          background-color: var(--color-gray);
        }

        &:hover {
          background-color: #35b472cc;

          .dictionary-indicator {
            color: var(--color-secondary);

            &:hover {
              color: var(--color-white);
            }
          }
        }

        .option-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.5rem;
        }

        .dictionary-indicator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.2rem;
          height: 1.2rem;
          color: var(--color-primary);
          opacity: 0.85;
          cursor: pointer;
        }

        .dictionary-indicator :global(svg) {
          width: 1rem;
          height: 1rem;
        }
      }
    }
  }
</style>
